import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Filter, X, Clock, BookOpen, Loader2 } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { gradeAccent } from '@/lib/brand';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SubjectHub: shared component for /curriculum/* pages.
// Props:
//   subject:   { label, icon, intro, accentColor, grades: [int], eraTags: [string] }
//   testidPrefix: string used to namespace data-testids per subject
//
// Behavior:
// - Lazy-loads units for each grade (parallel) and merges into a single grid
// - Filter rail on the left (Grade · Estimated Time)
// - Free-text inline filter
// - ResourceCards link to /unit/:id (same data, redesigned)
export default function SubjectHub({ subject, testidPrefix }) {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGrades, setActiveGrades] = useState(new Set(subject.grades));
  const [query, setQuery] = useState('');

  const loadUnits = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        subject.grades.map(g =>
          axios.get(`${API}/units/grade/${g}`).then(r => r.data).catch(() => [])
        )
      );
      setUnits(results.flat());
    } catch (_e) {
      setUnits([]);
    } finally {
      setLoading(false);
    }
  }, [subject.grades]);

  useEffect(() => { loadUnits(); }, [loadUnits]);

  const toggleGrade = (g) => {
    setActiveGrades(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next.size === 0 ? new Set(subject.grades) : next; // never empty
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return units
      .filter(u => activeGrades.has(u.grade_level))
      .filter(u => !q ||
        u.title?.toLowerCase().includes(q) ||
        u.description?.toLowerCase().includes(q));
  }, [units, activeGrades, query]);

  const Icon = subject.icon;

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid={`${testidPrefix}-hub`}>
        {/* Subject header */}
        <header className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center"
              style={{ backgroundColor: subject.accentColor + '22', color: subject.accentColor }}
            >
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Subject hub</p>
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-[#14213D] tracking-tight mb-3">{subject.label}</h1>
          <p className="text-sm text-[#5A6577] max-w-2xl leading-relaxed">{subject.intro}</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Filter rail */}
          <aside className="lg:col-span-3" data-testid={`${testidPrefix}-filter-rail`}>
            <div className="bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg p-5 sticky top-20">
              <header className="flex items-center justify-between mb-4">
                <h3 className="font-serif-display text-base text-[#14213D] inline-flex items-center gap-2">
                  <Filter className="h-3.5 w-3.5 text-[#7A93AC]" /> Filters
                </h3>
                {(activeGrades.size !== subject.grades.length || query) && (
                  <button
                    onClick={() => { setActiveGrades(new Set(subject.grades)); setQuery(''); }}
                    className="text-[11px] uppercase tracking-wider text-[#7A93AC] hover:text-[#14213D] inline-flex items-center gap-1"
                    data-testid={`${testidPrefix}-clear-filters`}
                  >
                    <X className="h-3 w-3" /> Reset
                  </button>
                )}
              </header>

              {/* Grade filter */}
              <div className="mb-5">
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] font-semibold mb-2">Grade level</p>
                <div className="space-y-1">
                  {subject.grades.map(g => (
                    <label key={g} className="flex items-center gap-2 px-2 py-1.5 hover:bg-white rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={activeGrades.has(g)}
                        onChange={() => toggleGrade(g)}
                        className="rounded border-[#EBE7E0] text-[#14213D] focus:ring-[#14213D]/30"
                        data-testid={`${testidPrefix}-grade-${g}`}
                      />
                      <span className="text-sm text-[#2D3748]">Grade {g}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Quick filter input */}
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-[#5A6577] font-semibold mb-2">Search within {subject.label}</p>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="title, topic…"
                  className="w-full bg-white border border-[#EBE7E0] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#14213D]/20 focus:border-[#14213D]/30"
                  data-testid={`${testidPrefix}-query-input`}
                />
              </div>
            </div>
          </aside>

          {/* Cards grid */}
          <section className="lg:col-span-9">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#5A6577]" data-testid={`${testidPrefix}-result-count`}>
                {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'lesson' : 'lessons'}`}
              </p>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-20 text-[#5A6577]">
                <Loader2 className="h-6 w-6 animate-spin mb-3" />
                <p className="text-sm">Loading curriculum…</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 bg-[#F9F8F4] border border-[#EBE7E0] rounded-lg">
                <BookOpen className="h-10 w-10 text-[#E2E8F0] mx-auto mb-3" />
                <h3 className="font-serif-display text-lg text-[#14213D] mb-2">No lessons match</h3>
                <p className="text-sm text-[#5A6577]">Adjust filters in the left rail to broaden your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={`${testidPrefix}-units-grid`}>
                {filtered.map((u) => <ResourceCard key={u.id} unit={u} onOpen={() => navigate(`/unit/${u.id}`)} subjectLabel={subject.label} />)}
              </div>
            )}
          </section>
        </div>
      </div>
    </AppShell>
  );
}

const ResourceCard = ({ unit, onOpen, subjectLabel }) => {
  const accent = gradeAccent(unit.grade_level);
  return (
  <article
    onClick={onOpen}
    className="group cursor-pointer bg-white border border-[#EBE7E0] rounded-md p-5 hover:-translate-y-1 hover:shadow-md hover:border-[#14213D]/20 transition-all relative overflow-hidden"
    data-testid={`resource-card-${unit.id}`}
  >
    <div className="absolute top-0 left-0 w-full h-0.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: accent.hex }} />
    <div className="absolute top-0 left-0 w-1 h-full" style={{ background: accent.hex, opacity: 0.7 }} />
    <div className="flex items-center gap-2 mb-3 flex-wrap pl-1.5">
      <span className="text-[11px] uppercase tracking-[0.15em] font-semibold" style={{ color: accent.hex }}>Grade {unit.grade_level}</span>
      <span className="text-[11px] text-[#5A6577]">·</span>
      <span className="text-[11px] text-[#7A93AC] font-medium">{subjectLabel}</span>
    </div>
    <h3 className="font-serif-display text-lg text-[#14213D] leading-tight mb-2 line-clamp-2 pl-1.5">{unit.title}</h3>
    <p className="text-sm text-[#5A6577] leading-relaxed line-clamp-3 mb-4 pl-1.5">{unit.description}</p>
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#EBE7E0] pl-1.5">
      <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded">Unit {unit.unit_number}</span>
      {unit.kansas_standards?.length > 0 && (
        <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded">{unit.kansas_standards.length} standards</span>
      )}
      {unit.vocabulary?.length > 0 && (
        <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded">{unit.vocabulary.length} terms</span>
      )}
      {unit.estimated_weeks && (
        <span className="text-[11px] bg-[#E2E8F0]/60 text-[#2D3748] px-2 py-1 rounded inline-flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" /> {unit.estimated_weeks}w
        </span>
      )}
      <span className="ml-auto text-xs font-medium text-[#14213D] group-hover:text-[#D4AF37] inline-flex items-center gap-1 transition-colors">
        Open <ArrowRight className="h-3 w-3" />
      </span>
    </div>
  </article>
  );
};
